/**
 * Contexto de las guías interactivas del panel admin.
 *
 * Se monta dentro de `DashboardLayout`, y ahí por dos razones: es el layout
 * que envuelve **todas** las rutas autenticadas del panel, y es quien monta
 * `CountryProvider` — sin el país seleccionado el motor no puede decidir qué
 * guías tienen sentido, porque casi todo lo operativo cuelga de él.
 *
 * El rol sale de `user.profiles.staff.role`, la misma fuente que usa el
 * `Sidebar` para pintar el menú.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useCountry } from '@/contexts/CountryContext';
import { TourContext, type TourContextValue } from './TourContext';
import { TourPopover } from './components/TourPopover';
import { TourWaiting } from './components/TourWaiting';
import { TourSpotlight } from './components/TourSpotlight';
import { useTargetRect } from './components/useTargetRect';
import { progressKey, readProgress } from './progress';
import { FLOWS, findFlow } from './registry';
import { findFlowForPath, selectFlows } from './selectFlows';
import { useTourEngine } from './useTourEngine';
import type { TourContext as TourCtx } from './types';
import type { StaffRole } from '@/types/staff.types';

/** Cuánto puede tardar un paso antes de que haga falta decir que sigue vivo. */
const MS_ANTES_DE_AVISAR = 400;

export function TourProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { selectedCountryId, selectedCountry } = useCountry();
  const location = useLocation();

  const staff = user?.profiles?.staff ?? null;
  const role = staff?.role as StaffRole | undefined;

  const ctx: TourCtx = useMemo(
    () => ({
      role,
      countryId: selectedCountryId || undefined,
      countryName: selectedCountry?.name,
    }),
    [role, selectedCountryId, selectedCountry?.name],
  );

  const key = progressKey(staff?.id);

  const [completed, setCompleted] = useState<string[]>(() => readProgress(key).completed);

  const onCompleted = useCallback(() => {
    setCompleted(readProgress(key).completed);
  }, [key]);

  const engine = useTourEngine({ ctx, progressKey: key, onCompleted });
  const rect = useTargetRect(engine.targetEl);

  const availableFlows = useMemo(() => selectFlows(FLOWS, ctx), [ctx]);
  const flowForCurrentPath = useMemo(
    () => findFlowForPath(FLOWS, ctx, location.pathname),
    [ctx, location.pathname],
  );

  /**
   * Lo que el botón de la esquina ofrece.
   *
   * La de esta pantalla y, si no la tiene, la primera disponible — **sin
   * mirar si ya se hizo**. Antes se saltaba las completadas, así que en
   * cuanto el operador terminaba las guías el botón desaparecía de todas las
   * pantallas que no tienen la suya, y no quedaba ninguna forma de repasar.
   */
  const suggestedFlow = useMemo(
    () => flowForCurrentPath ?? availableFlows[0],
    [flowForCurrentPath, availableFlows],
  );

  const startFlow = useCallback(
    (flowId: string) => {
      const flow = findFlow(flowId);
      if (flow) engine.start(flow);
    },
    [engine],
  );

  const value = useMemo<TourContextValue>(
    () => ({
      availableFlows,
      flowForCurrentPath,
      suggestedFlow,
      completed,
      isRunning: engine.flow !== null,
      startFlow,
      stopFlow: engine.stop,
    }),
    [
      availableFlows,
      flowForCurrentPath,
      suggestedFlow,
      completed,
      engine.flow,
      engine.stop,
      startFlow,
    ],
  );

  const showOverlay = isAuthenticated && engine.flow !== null && engine.status === 'active';

  /**
   * Preparar un paso puede tardar —navegar, descargar la pantalla, esperar al
   * elemento— y durante ese rato no hay nada que pintar. Antes eso dejaba la
   * guía **invisible**, y el operador concluía que se había cerrado: se iba a
   * otra pantalla y el paso reaparecía allí.
   *
   * Se avisa de que sigue viva, pero **sólo si la espera se nota**. Pintarlo
   * desde el primer milisegundo haría parpadear un cartel en cada paso, que es
   * peor que la espera.
   */
  //
  // Se guarda **qué paso** llegó a tardar, no un simple sí/no. Con un
  // booleano hacía falta apagarlo al salir de la preparación, y apagarlo
  // desde el efecto es una cascada de renders —lo que avisa
  // `react-hooks/set-state-in-effect`—. Comparando contra el paso actual, el
  // valor que quedó de un paso anterior sencillamente ya no coincide.
  const preparando =
    isAuthenticated && engine.flow !== null && engine.status === 'preparing';
  const episodio = preparando ? `${engine.flow?.id}:${engine.index}` : null;

  const [tardo, setTardo] = useState<string | null>(null);

  useEffect(() => {
    if (!episodio) return;
    const t = setTimeout(() => setTardo(episodio), MS_ANTES_DE_AVISAR);
    return () => clearTimeout(t);
  }, [episodio]);

  const esperaVisible = episodio !== null && tardo === episodio;

  return (
    <TourContext.Provider value={value}>
      {children}
      {createPortal(
        <AnimatePresence>
          {esperaVisible && (
            <TourWaiting key="waiting" onClose={engine.stop} />
          )}
          {showOverlay && engine.step && (
            <>
              <TourSpotlight key="spotlight" rect={rect} />
              <TourPopover
                key={`${engine.flow?.id}:${engine.step.id}`}
                title={engine.step.title}
                body={engine.step.body}
                stepNumber={engine.index + 1}
                stepCount={engine.steps.length}
                rect={rect}
                placement={engine.step.placement}
                isFirst={engine.isFirst}
                isLast={engine.isLast}
                onNext={engine.next}
                onPrev={engine.prev}
                onClose={engine.stop}
              />
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </TourContext.Provider>
  );
}
